import { Component, inject, ViewChild } from '@angular/core';
import { ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { ImageModalData } from '@models/modals-datas/image-modal-data/image-modal-data.interface';
import { PopupData } from '@constants/popup-data.constants';
import { ImageUploadService } from '@core/api-services';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

const MIN_SCALE = 0.1;
const MAX_SCALE = 3;
const SCALE_STEP = 0.1;

@Component({
  selector: 'app-image-upload-modal',
  templateUrl: './image-upload-modal.component.html',
  styleUrls: ['./image-upload-modal.component.scss'],
  standalone: false,
})
export class ImageUploadModalComponent {
  public data = inject<ImageModalData>(MAT_DIALOG_DATA);

  @ViewChild(ImageCropperComponent)
  private imageCropper?: ImageCropperComponent;

  public imageChangedEvent?: Event;
  public isImageLoaded: boolean = !this.data.imageUrl;
  public canvasRotation: number = 0;
  public scale: number = 1;
  public transform: ImageTransform = {};
  public uploadFailed: boolean = false;
  public isNotImage: boolean = false;
  public isInvalidImage: boolean = false;
  public popupData = PopupData;

  private _preview = false;

  constructor(
    private imageUploadService: ImageUploadService,
    private dialogRef: MatDialogRef<ImageUploadModalComponent>,
  ) {}

  public async fileChangeEvent(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.match('image')) {
      this.isNotImage = true;
      // allow re-selecting the same (invalid) file later
      input.value = '';
      return;
    }

    this.isNotImage = false;
    this.imageChangedEvent = event;
    this.uploadFailed = false;
    this._preview = true;
  }

  public async compressFile(base64: string): Promise<string> {
    let file: File | undefined;
    const files: FileList | undefined =
      (this.imageChangedEvent?.target as HTMLInputElement | undefined)?.files ??
      undefined;

    if (files?.length) {
      file = files[0];
    }

    return this.imageUploadService.compressFile(
      file || this.imageUploadService.base64ToFile(base64),
      base64,
    );
  }

  public loadImageFailed(): void {
    this.data.imageUrl = null;
    this.isInvalidImage = true;
    this.uploadFailed = true;
    this.isImageLoaded = true;
  }

  /**
   * Passes image to upload service
   */
  public async processFile(): Promise<void> {
    // Use the cropper instance safely
    if (!this.imageCropper) {
      this.uploadFailed = true;
      return;
    }

    // Generate the snapshot slice from the preloaded canvas engine
    const croppedImage = this.imageCropper.crop();

    if (!croppedImage || !croppedImage.base64) {
      this.uploadFailed = true;
      console.error(
        'Cropper state empty. Verify that your urlToBase64 pipe is outputting valid base64 data.',
      );
      return;
    }

    try {
      const result = await this.compressFile(croppedImage.base64);
      this.dialogRef.close(result);
    } catch (error) {
      this.uploadFailed = true;
      console.error('File compression pipeline execution crashed:', error);
    }
  }

  public imageLoaded(): void {
    this.isImageLoaded = true;
  }

  public resetImage(): void {
    this.scale = 1;
    this.canvasRotation = 0;
    this.transform = {};
  }

  public rotateLeft(): void {
    this.canvasRotation = (this.canvasRotation - 1 + 4) % 4;
  }

  public rotateRight(): void {
    this.canvasRotation = (this.canvasRotation + 1) % 4;
  }

  public zoomOut(): void {
    this.scale = Math.max(MIN_SCALE, +(this.scale - SCALE_STEP).toFixed(2));
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  public zoomIn(): void {
    this.scale = Math.min(MAX_SCALE, +(this.scale + SCALE_STEP).toFixed(2));
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  public close(): void {
    this.dialogRef.close(this.data.imageUrl);
  }

  get title(): string {
    let title = this.data.imageType.replace(/_/g, ' ');
    title = title.charAt(0).toUpperCase() + title.slice(1);
    title += ' Image';
    return title;
  }
}
